import React, { useEffect, useReducer, useState } from "react";
import { Button } from "components/Buttons/Buttons";
import { Avatar, Dropdown, Menu, Modal, Select, Space, Table } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import useSettingsHeader from "hooks/useSettingsHeader";
import { useDispatch } from "react-redux";
import {
  getAllLoyaltyRewardCatalogItems,
  updateLoyaltyRewardCatalogItem,
} from "services/loyaltyRewardCatalog.service";
import { getAllBoosts, getAllServices } from "services/servicesAndBoostsSettings.service";
import { getAllCategories } from "services/categoriesSettings.services";
import SearchAndFilterColumns from "components/SearchAndFilterColumns/SearchAndFilterColumns";
import usePortal from "hooks/usePortal";
import LoyaltyRewardCatalogAddEditModal from "./components/LoyaltyRewardCatalogAddEditModal";
import { BENEFIT_TYPE, getEnumKeyByValue } from "utils/enums";

const reducer = (state, action) => {
  switch (action.type) {
    case "SET":
      return action.data;
    default:
      return state;
  }
};

function LoyaltyRewardCatalog() {
  const [showAddEditModal, setShowAddEditModal] = useState();
  const [data, localDispatch] = useReducer(reducer, []);

  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [activeFilter, setActiveFilter] = useState("active"); // all, active, inactive
  const [searchText, setSearchText] = useState("");

  const [products, setProducts] = useState({
    isLoading: true,
    data: [],
  });
  const [categories, setCategories] = useState({
    isLoading: true,
    data: [],
  });

  const dispatch = useDispatch();

  const loadData = async () => {
    const result = await dispatch(getAllLoyaltyRewardCatalogItems());
    if (result?.status === "success") {
      localDispatch({
        type: "SET",
        data: result.data,
      });
    }
  };

  useEffect(() => {
    loadData();
    Promise.all([
      dispatch(getAllServices(false)),
      dispatch(getAllBoosts(false)),
    ]).then((result) => {
      const [resultServices, resultBoosts] = result;
      const services = resultServices?.status === "success" ? resultServices.data ?? [] : [];
      const boosts = resultBoosts?.status === "success" ? resultBoosts.data ?? [] : [];
      setProducts({
        isLoading: false,
        data: [
          ...services.map((it) => ({ ...it, productName: `${it.productName} (Service)` })),
          ...boosts.map((it) => ({ ...it, productName: `${it.productName} (Boost)` })),
        ],
      });
    });
    dispatch(getAllCategories(false)).then((result) => {
      if (result?.status === "success") {
        setCategories({
          isLoading: false,
          data: result.data ?? [],
        });
      }
    });
  }, []);

  const handleToggleActive = (record) => {
    const {
      loyaltyRewardCatalogItemId,
      name,
      description,
      productId,
      pointsCost,
      imageUrl,
      imageBase64String,
      imageExt,
      benefitType,
      benefitValue,
      isActive,
      facilityExcludeType,
      productExcludeType,
      categoryExcludeType,
      facilityIds,
      productIds,
      categoryIds,
    } = record;
    dispatch(
      updateLoyaltyRewardCatalogItem(loyaltyRewardCatalogItemId, {
        name,
        description,
        productId,
        pointsCost,
        imageUrl,
        imageBase64String,
        imageExt,
        benefitType,
        benefitValue,
        facilityExcludeType,
        productExcludeType,
        categoryExcludeType,
        facilityIds,
        productIds,
        categoryIds,
        isActive: !isActive,
      })
    ).then((result) => {
      if (result?.status === "success") {
        loadData();
      }
    });
  };

  const checkActiveFilter = (item) => {
    if (activeFilter === "all") return true;
    else if (activeFilter === "active" && item?.isActive) return true;
    else if (activeFilter === "inactive" && !item?.isActive) return true;
    return false;
  };

  const inactivateConfirmation = (record) => ({
    title: "Inactivate Reward",
    content: (
      <>Are you sure that you want to inactivate {record?.name ? `"${record.name}"` : "this reward"}?</>
    ),
    centered: true,
    maskClosable: true,
    onOk: (close) => {
      handleToggleActive(record);
      close();
    },
  });

  const activateConfirmation = (record) => ({
    title: "Activate Reward",
    content: (
      <>Are you sure that you want to activate {record?.name ? `"${record.name}"` : "this reward"}?</>
    ),
    centered: true,
    maskClosable: true,
    onOk: (close) => {
      handleToggleActive(record);
      close();
    },
  });

  const settingHeaderContent = useSettingsHeader(
    <Button
      rounded={true}
      onClick={() => {
        setShowAddEditModal(true);
      }}
    >
      Create Rewards/Redemption
    </Button>
  );

  const columns = [
    {
      title: "Image",
      key: "imageUrl",
      width: 80,
      render: (record) => <Avatar shape="square" src={record?.imageUrl} />,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => {
        if (a.name?.toLowerCase?.() < b.name?.toLowerCase?.()) return -1;
        if (a.name?.toLowerCase?.() > b.name?.toLowerCase?.()) return 1;
        return 0;
      },
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      sorter: (a, b) => {
        if (a.productName?.toLowerCase?.() < b.productName?.toLowerCase?.()) return -1;
        if (a.productName?.toLowerCase?.() > b.productName?.toLowerCase?.()) return 1;
        return 0;
      },
    },
    {
      title: "Points Cost",
      dataIndex: "pointsCost",
      key: "pointsCost",
      sorter: (a, b) => a.pointsCost - b.pointsCost,
      render: (value) => Number(value).toFixed(2),
    },
    {
      title: "Benefit Type",
      dataIndex: "benefitType",
      key: "benefitType",
      render: (value) => getEnumKeyByValue(BENEFIT_TYPE, value) ?? "-",
    },
    {
      title: "Benefit Value",
      dataIndex: "benefitValue",
      key: "benefitValue",
      render: (value) => (value === null || value === undefined ? "-" : value),
    },
    {
      title: "Active",
      key: "active",
      width: 100,
      render: (record) => (record?.isActive ? "Active" : "Inactive"),
    },
    {
      title: "",
      key: "action",
      align: "right",
      width: 80,
      render: (record) => {
        return (
          <Dropdown
            overlay={
              <Menu style={{ borderRadius: 6, border: "2px solid #CDD5D8 " }}>
                <Menu.Item onClick={() => setShowAddEditModal(record)} key="edit">
                  Edit
                </Menu.Item>
                <Menu.Item
                  key="toggle"
                  onClick={() => {
                    if (record?.isActive) Modal.warning(inactivateConfirmation(record));
                    else Modal.warning(activateConfirmation(record));
                  }}
                >
                  {record?.isActive ? "Inactivate" : "Activate"}
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
            getPopupContainer={(triggerNode) => triggerNode?.parentNode}
          >
            <MoreOutlined style={{ fontSize: 18 }} />
          </Dropdown>
        );
      },
    },
  ];

  const searchAndFiltersContent = usePortal(
    "#settingHeader .settingsHeaderLeftSideAddon",
    <Space>
      <SearchAndFilterColumns
        columns={columns}
        hiddenColumns={hiddenColumns}
        setHiddenColumns={(columnTitles) => {
          setHiddenColumns(columnTitles);
        }}
        searchText={searchText}
        setSearchText={(text) => {
          setSearchText(text);
        }}
      />
      <Select
        style={{ width: 120 }}
        value={activeFilter}
        onChange={(value) => {
          setActiveFilter(value);
        }}
        options={[
          {
            value: "all",
            label: "All",
          },
          {
            value: "active",
            label: "Active",
          },
          {
            value: "inactive",
            label: "Inactive",
          },
        ]}
      />
    </Space>
  );

  return (
    <div>
      {settingHeaderContent}
      {searchAndFiltersContent}
      <Table
        rowKey={(data) => data.loyaltyRewardCatalogItemId}
        tableLayout="auto"
        locale={{ emptyText: "No Reward Catalog Items Found" }}
        dataSource={data?.filter(
          (it) =>
            checkActiveFilter(it) &&
            `${it?.name ?? ""} ${it?.productName ?? ""}`
              .toLowerCase()
              .includes(searchText.toLowerCase())
        )}
        columns={columns.filter((column) => {
          if (!hiddenColumns.includes(column.title)) return true;
          return false;
        })}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [15, 25, 50, 100, 250],
        }}
      />

      <LoyaltyRewardCatalogAddEditModal
        data={showAddEditModal}
        products={products.data}
        isProductsLoading={products.isLoading}
        categories={categories.data}
        isCategoriesLoading={categories.isLoading}
        visible={showAddEditModal}
        onCancel={() => {
          setShowAddEditModal();
        }}
        onSuccess={() => {
          setShowAddEditModal();
          loadData();
        }}
      />
    </div>
  );
}

export default LoyaltyRewardCatalog;
